import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ICON_BUTTON_ICONS } from '../buttons/IconButton';

/**
 * One draggable row of a "Customize columns" list: a drag handle plus a
 * checkbox-style selectable area.
 *
 * Was defined identically — byte for byte — in both `SearchResults/index.jsx` and
 * `ProfilerProjects/components/ProfilerSearchResultsTabView.jsx`; hoisted here
 * under TD-2746 so the two surfaces share one definition.
 *
 * `item.disableSelect` marks a column the analyst may reorder but not hide: the
 * area renders as a filled-but-disabled checkbox and swallows the click.
 *
 * The classNames are load-bearing — `search-results.less` and `projects.less`
 * both select on `.customize-columns-list-item` / `.selectable-area`.
 */
// A column the analyst may reorder but not hide reads as permanently checked.
const checkboxIcon = (item) => {
  if (item.disableSelect) return ICON_BUTTON_ICONS.CHECK_SQUARE;
  return item.selected ? ICON_BUTTON_ICONS.CHECK_SQUARE_FILL : ICON_BUTTON_ICONS.SQUARE;
};

const SortableColumnItem = observer(({ item, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const toggle = () => {
    if (item.disableSelect) return;
    onClick();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`customize-columns-list-item ${isDragging ? 'dragging' : ''}`}
    >
      <span className="handle" {...listeners} {...attributes}>
        ☰
      </span>
      {/* A checkbox in behaviour, so it carries the checkbox role and keyboard
          contract rather than only a click handler (TD-2746). */}
      <div
        className={`selectable-area ${item.selected ? 'selected' : ''} ${item.disableSelect ? 'disabled' : ''}`}
        role="checkbox"
        aria-checked={item.disableSelect ? true : !!item.selected}
        aria-disabled={!!item.disableSelect}
        tabIndex={item.disableSelect ? -1 : 0}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key !== ' ' && event.key !== 'Enter') return;
          event.preventDefault();
          toggle();
        }}
      >
        <div className="img-wrap">
          {/* Decorative: the checked state is on the role="checkbox" above. */}
          <img src={checkboxIcon(item)} alt="" />
        </div>
        <div className="name-wrap">{item.name}</div>
      </div>
    </div>
  );
});

SortableColumnItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    selected: PropTypes.bool,
    disableSelect: PropTypes.bool
  }).isRequired,
  onClick: PropTypes.func.isRequired
};

export default SortableColumnItem;
